import React, { useState } from "react";
import { moneyCompact } from "@/lib/format";
import { Plus, X, GitCompare } from "lucide-react";
import { VersionCompareDialog } from "@/components/VersionCompareDialog";

const TYPE_STYLE = {
  revision: "bg-[#F4C400] text-black",
  change_order: "bg-indigo-500 text-white",
  alternate: "bg-emerald-500 text-white",
};
const ADD_OPTS = [
  ["revision", "New revision"],
  ["change_order", "Change order"],
  ["alternate", "Alternate"],
];

export function VersionTabs({ versions, activeVersionId, viewingId, locked, canEdit, onSelect, onCreate, onDelete, bidId, whyNote, onWhyNote }) {
  const [menu, setMenu] = useState(false);
  const [compare, setCompare] = useState(false);
  const hasSealed = versions.some((v) => v.version_id !== activeVersionId);
  return (
    <div className="shrink-0 h-12 border-t border-slate-200 bg-white flex items-center gap-1.5 px-3 relative z-40" data-testid="version-tabs">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 shrink-0 mr-1">Versions</span>
      {versions.length > 1 && (
        <button data-testid="version-compare" onClick={() => setCompare(true)} title="What changed, where, why"
          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 text-[#003E87] hover:border-[#003E87] transition">
          <GitCompare className="w-3.5 h-3.5" /> Compare
        </button>
      )}
      {bidId && <VersionCompareDialog open={compare} onOpenChange={setCompare} bidId={bidId} versions={versions} />}
      <div className="flex items-center gap-1.5 overflow-x-auto min-w-0">
      {versions.map((v) => {
        const active = v.version_id === activeVersionId;
        const viewing = v.version_id === viewingId;
        return (
          <div key={v.version_id} data-testid={`version-tab-${v.label}`} onClick={() => onSelect(v)}
            className={`group relative shrink-0 flex items-center gap-2 rounded-t-md border-t-2 px-3 py-1.5 cursor-pointer transition ${viewing ? "bg-slate-100 border-[#003E87]" : "bg-white border-transparent hover:bg-slate-50"}`}>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${TYPE_STYLE[v.type] || "bg-slate-200 text-slate-700"}`}>{v.label}</span>
            <span className="text-xs font-mono text-slate-600" title={v.award_note || ""}>{v.type === "change_order" && v.total != null ? `+${moneyCompact(v.total)}` : v.total != null ? moneyCompact(v.total) : "—"}</span>
            {v.standalone_total != null && <span className="text-[9px] text-slate-400 font-mono" data-testid={`version-award-${v.label}`} title={v.award_note || ""}>sites alone {moneyCompact(v.standalone_total)} · credit −{moneyCompact(v.portfolio_credit || 0)}</span>}
            {v.priced && v.priced !== "in_house" && <span className="text-[9px] px-1 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid={`version-priced-${v.label}`}>{v.priced === "elsewhere" ? "priced elsewhere" : "imported"}</span>}
            {(v.client_due_date || v.sent_at) && (
              <span className="text-[9px] text-slate-400 font-mono" title={`client due ${v.client_due_date || "—"} · internal due ${v.internal_due_date || "—"}${v.sent_at ? ` · sent ${v.sent_at.slice(0, 10)}` : ""}`} data-testid={`version-dates-${v.label}`}>
                {v.sent_at ? `sent ${v.sent_at.slice(5, 10)}` : v.client_due_date ? `due ${v.client_due_date.slice(5, 10)}` : ""}{v.late ? " · late" : ""}
              </span>
            )}
            {active
              ? <span className="text-[9px] uppercase tracking-wide text-emerald-600 font-semibold">{locked ? "sent" : "live"}</span>
              : <span className="text-[9px] uppercase tracking-wide text-slate-400">{v.sealed ? "sealed" : "locked"}</span>}
            {canEdit && versions.length > 1 && !v.protected && (
              <button data-testid={`version-delete-${v.label}`} onClick={(e) => { e.stopPropagation(); onDelete(v); }}
                className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-600" title={`Delete ${v.label} (never sent, no approval, no documents)`}>
                <X className="w-3 h-3" />
              </button>
            )}
            {canEdit && versions.length > 1 && v.protected && (
              <span data-testid={`version-protected-${v.label}`} className="opacity-0 group-hover:opacity-100 text-[9px] text-slate-400" title={v.protected}>kept</span>
            )}
          </div>
        );
      })}
      </div>
      {canEdit && hasSealed && !locked && (
        <input data-testid="version-why-box" value={whyNote || ""} onChange={(e) => onWhyNote?.(e.target.value)} placeholder="Why this change? (saved with every edit, shows on Compare)"
          className="ml-2 h-7 flex-1 min-w-[160px] max-w-md text-[11px] px-2 rounded border border-amber-300 bg-amber-50/60 focus:outline-none focus:border-[#003E87]" title="One line: the reason for the edits you are making on this version" />
      )}
      {canEdit && (
        <div className="relative shrink-0">
          <button data-testid="version-add" onClick={() => setMenu((m) => !m)} title="Add a version"
            className="ml-1 w-7 h-7 grid place-items-center rounded-full border border-slate-200 text-slate-500 hover:border-[#003E87] hover:text-[#003E87] transition">
            <Plus className="w-4 h-4" />
          </button>
          {menu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
              <div className="absolute bottom-full mb-1 right-0 w-72 bg-white rounded-lg shadow-xl border border-slate-200 p-1 z-[70]" data-testid="version-add-menu">
                {ADD_OPTS.map(([t, l]) => (
                  <button key={t} data-testid={`version-add-${t}`} onClick={() => { setMenu(false); onCreate(t); }}
                    className="w-full text-left text-sm px-3 py-2 rounded hover:bg-slate-50 text-slate-700">{l}</button>
                ))}
                <div className="border-t border-slate-100 mt-1 pt-1 px-3 py-2 text-[11px] leading-snug text-slate-500" data-testid="version-howto">
                  <b>How versions work.</b> New revision / Alternate: copy from an earlier version or start blank (bid setup only). Change order: starts from the sent revision and holds only the added or deducted scope; its total prints as “CO1: +$X” against the base. A sent version is sealed for ever — every edit lands on the active tab. Each version keeps its own due dates, send, approval and stamps. <b>Compare</b> shows what changed, where and why; type the reason in the Why box while editing. On send, “Revision brief &lt;from&gt; → &lt;to&gt;.pdf” is filed under Internal and this round's client files sit in Sent to client/&lt;label&gt;. <a href="/how-to/versions" className="underline text-[#003E87]" data-testid="version-howto-link">Full How-to</a>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
